import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getPushCapabilities,
  getPushPermissionStatus,
  isStandalone,
  requestPushPermission,
  subscribeToPush,
} from '@/lib/push/subscribe';

/**
 * Push Permission Prompt — Family (CLAUDE.md §6 madde 9)
 *
 * Custom in-app prompt shown only when ALL of the following hold:
 *   - The app is running in standalone mode (PWA installed). Browsers — and
 *     iOS especially — silently fail outside of PWA install, so prompting
 *     before A2HS would just teach users to tap "Don't allow".
 *   - The browser supports Push API + Notifications.
 *   - VAPID public key is configured (otherwise we cannot subscribe).
 *   - `Notification.permission === 'default'` (we have not asked yet).
 *   - The user has not dismissed it before (localStorage flag).
 *
 * Tradeoff: we DO NOT auto-prompt when permission was previously denied. The
 * user has to take an explicit action to re-enable it (system settings) which
 * we explain in the FAQ.
 *
 * UX: warm, family-appropriate copy. Two big buttons. iOS gestures work
 * because requestPermission() runs synchronously inside the click handler.
 *
 * Accessibility (CLAUDE.md §7 ARIA mandatory):
 *   - role="dialog" + aria-modal + aria-labelledby
 *   - ESC closes the dialog (treated as "Later")
 *   - Focus moves to the primary "Allow" button on open and is trapped inside
 *     the dialog while it is mounted (Tab cycles between Allow ↔ Later).
 */

const ASKED_KEY = 'family.push.askedAt';

function hasAskedBefore(): boolean {
  if (typeof localStorage === 'undefined') return false;
  return Boolean(localStorage.getItem(ASKED_KEY));
}

function markAsked(): void {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(ASKED_KEY, String(Date.now()));
  }
}

export function PushPermissionPrompt() {
  const { t } = useTranslation();
  const [open, setOpen] = useState<boolean>(false);
  const [busy, setBusy] = useState<boolean>(false);
  const grantButtonRef = useRef<HTMLButtonElement | null>(null);
  const laterButtonRef = useRef<HTMLButtonElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    // Only relevant inside the installed PWA. Browser-tab usage skips this.
    if (!isStandalone()) return;
    if (hasAskedBefore()) return;

    const caps = getPushCapabilities();
    if (!caps.supported) return;
    if (getPushPermissionStatus() !== 'default') return;

    // Slight delay so the user lands on Home first; prompts that fire during
    // first paint feel jarring on small screens.
    const timer = setTimeout(() => setOpen(true), 1200);
    return () => clearTimeout(timer);
  }, []);

  // Focus management: remember the previously-focused element, focus the
  // primary action on open, then restore focus on close.
  useEffect(() => {
    if (!open) return;
    if (typeof document !== 'undefined') {
      previouslyFocused.current = document.activeElement as HTMLElement | null;
    }
    // Focus the primary action so screen reader + keyboard users get sent
    // straight to the affirmative path. Older users with a Bluetooth keyboard
    // also benefit.
    const id = window.setTimeout(() => grantButtonRef.current?.focus(), 50);
    return () => {
      window.clearTimeout(id);
      previouslyFocused.current?.focus?.();
    };
  }, [open]);

  // ESC closes; Tab cycles between the two buttons (basic focus trap that
  // does not need a full focus-trap library for two interactive elements).
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !busy) {
        e.preventDefault();
        handleLater();
        return;
      }
      if (e.key !== 'Tab') return;
      const grant = grantButtonRef.current;
      const later = laterButtonRef.current;
      if (!grant || !later) return;
      const active = document.activeElement;
      if (e.shiftKey) {
        if (active === grant) {
          e.preventDefault();
          later.focus();
        }
      } else {
        if (active === later) {
          e.preventDefault();
          grant.focus();
        }
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, busy]);

  async function handleGrant() {
    if (busy) return;
    setBusy(true);
    try {
      const permission = await requestPushPermission();
      markAsked();
      if (permission === 'granted') {
        // Fire-and-forget; subscribeToPush logs its own failures.
        await subscribeToPush();
      }
    } finally {
      setBusy(false);
      setOpen(false);
    }
  }

  function handleLater() {
    markAsked();
    setOpen(false);
  }

  if (!open) return null;

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="push-permission-title"
      aria-describedby="push-permission-body"
      className="fixed inset-0 z-40 flex items-end md:items-center justify-center bg-black/40 backdrop-blur-sm"
    >
      <div className="w-full md:max-w-md md:rounded-2xl rounded-t-2xl bg-bg-elevated border-t md:border border-accent-gold p-6 shadow-warm-lg">
        <p className="eyebrow mb-1">{t('push.permissionTitle')}</p>
        <h2 id="push-permission-title" className="font-serif italic text-2xl text-text-primary mb-3">
          {t('push.permissionTitle')}
        </h2>
        <div className="ornament-divider mb-3" aria-hidden="true">
          <span>✦</span>
        </div>
        <p id="push-permission-body" className="mb-6 text-base text-text-secondary font-serif italic leading-relaxed">
          {t('push.permissionBody')}
        </p>
        <div className="flex flex-col gap-3">
          <button
            ref={grantButtonRef}
            type="button"
            onClick={() => void handleGrant()}
            disabled={busy}
            className="w-full min-h-tap px-6 py-3 rounded-xl bg-accent-primary text-white text-base font-semibold border border-accent-gold shadow-warm-sm hover:bg-accent-primary/90 disabled:opacity-60 focus-visible:ring-2 focus-visible:ring-accent-primary focus-visible:ring-offset-2"
          >
            {t('push.permissionGrant')}
          </button>
          <button
            ref={laterButtonRef}
            type="button"
            onClick={handleLater}
            disabled={busy}
            className="w-full min-h-tap px-6 py-3 rounded-xl border border-accent-gold text-base text-accent-gold-dark hover:bg-bg-bubble-received/40 disabled:opacity-60 focus-visible:ring-2 focus-visible:ring-accent-primary focus-visible:ring-offset-2 font-serif italic"
          >
            {t('push.permissionLater')}
          </button>
        </div>
      </div>
    </div>
  );
}
